/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * atom 活预览演示层：
 * - evalAtomClass：把 UMD atom 源码在受控沙箱中 eval，取出 PlayableAtoms.<ClassName>
 * - createAtomDemo：按 atom key 返回一个可交互演示（update/render/actions/getState）
 */
import {
  DemoEnv, DemoHandle, drawStubGrid, eventPos, fillRound, label, LOGICAL_W,
  makeGem, makeSpinFrames, makeStubGrid, PALETTE, silentWavUrl, valColor,
} from './demo-utils';

const classCache = new Map<string, any>();

/**
 * 沙箱 eval atom 源码。UMD 尾部引用 window，这里以形参遮蔽为受控 sandbox；
 * 源码内部对 document/Image/navigator 等的引用沿作用域链落到真实全局，行为与生产一致。
 */
export function evalAtomClass(source: string, className: string): any {
  const hit = classCache.get(className);
  if (hit) return hit;
  const sandbox: Record<string, any> = { devicePixelRatio: window.devicePixelRatio || 1 };
  sandbox.window = sandbox;
  sandbox.self = sandbox;
  sandbox.globalThis = sandbox;
  const factory = new Function('window', 'global', 'self', source);
  factory(sandbox, sandbox, sandbox);
  const cls = sandbox.PlayableAtoms?.[className];
  if (!cls) throw new Error(`atom 源码未导出 PlayableAtoms.${className}`);
  classCache.set(className, cls);
  return cls;
}

type Factory = (Cls: any, env: DemoEnv) => DemoHandle;

function onTap(env: DemoEnv, cb: (x: number, y: number) => void): () => void {
  const h = (e: Event) => {
    const p = eventPos(env.canvas, e as PointerEvent);
    cb(p.x, p.y);
  };
  env.canvas.addEventListener('pointerdown', h);
  return () => env.canvas.removeEventListener('pointerdown', h);
}

function randVal(max = 5): number {
  return 1 + Math.floor(Math.random() * max);
}

/* ------------------------------------------------------------------ */
/* 各 atom 演示工厂                                                    */
/* ------------------------------------------------------------------ */

const demos: Record<string, Factory> = {
  /* ----- grid ----- */
  'rect-grid': (Cls, env) => {
    const grid = new Cls(6, 6, 44, 18, 52);
    grid.forEachCell((c: number, r: number) => grid.setCell(c, r, randVal()));
    let taps = 0;
    const off = onTap(env, (x, y) => {
      const { col, row } = grid.pixelToCell(x, y);
      if (grid.isValid(col, row)) {
        grid.setCell(col, row, (grid.getCell(col, row) % 5) + 1);
        taps++;
      }
    });
    return {
      hint: '点击格子循环切换其值',
      render: (ctx) => {
        grid.render(ctx, (c: any, x: number, y: number, s: number, v: number) => {
          fillRound(c, x + 2, y + 2, s - 4, s - 4, 8, valColor(v));
        });
      },
      getState: () => ({ 规格: '6×6', 点击次数: taps, 接口: 'pixelToCell/isValid/setCell' }),
      destroy: off,
    };
  },

  'hex-grid': (Cls, env) => {
    const grid = new Cls(6, 5, 26, 30, 70);
    const vals = new Map<string, number>();
    let taps = 0;
    const off = onTap(env, (x, y) => {
      const cell = grid.pixelToCell(x, y);
      if (cell && grid.isValid(cell.col, cell.row)) {
        const k = `${cell.col},${cell.row}`;
        vals.set(k, ((vals.get(k) || 0) % 5) + 1);
        taps++;
      }
    });
    return {
      hint: '点击六边形切换值',
      render: (ctx) => {
        grid.render(ctx, (c: any, cx: number, cy: number, r: number, _v: any, col: number, row: number) => {
          const v = vals.get(`${col},${row}`) || 0;
          c.beginPath();
          for (let i = 0; i < 6; i++) {
            const a = (Math.PI / 3) * i - Math.PI / 6;
            const px = cx + (r - 2) * Math.cos(a);
            const py = cy + (r - 2) * Math.sin(a);
            if (i === 0) c.moveTo(px, py); else c.lineTo(px, py);
          }
          c.closePath();
          c.fillStyle = v ? valColor(v) : '#1e293b';
          c.fill();
        });
      },
      getState: () => ({ 点击次数: taps, 已赋值格: vals.size }),
      destroy: off,
    };
  },

  'free-layout': (Cls, env) => {
    const layout = new Cls(LOGICAL_W, 340);
    let n = 0;
    let selected: string | null = null;
    const add = () => {
      n++;
      layout.addEntity(`e${n}`, 30 + Math.random() * 240, 30 + Math.random() * 260, 48, 48, { v: randVal() });
    };
    for (let i = 0; i < 4; i++) add();
    const off = onTap(env, (x, y) => {
      const hit = layout.hitTest ? layout.hitTest(x, y) : null;
      selected = hit ? (hit.id || hit) : null;
    });
    return {
      hint: '点击方块选中，按钮可追加实体',
      actions: [{ label: '添加实体', run: add }],
      render: (ctx) => {
        layout.render(ctx, (c: any, e: any) => {
          fillRound(c, e.x, e.y, e.width ?? e.w ?? 48, e.height ?? e.h ?? 48, 8, valColor(e.data?.v || 1));
          if (selected && (e.id === selected)) {
            c.strokeStyle = '#fff';
            c.lineWidth = 2;
            c.strokeRect(e.x - 2, e.y - 2, (e.width ?? e.w ?? 48) + 4, (e.height ?? e.h ?? 48) + 4);
          }
        });
      },
      getState: () => ({ 实体数: n, 选中: selected || '无' }),
      destroy: off,
    };
  },

  'scroll-layout': (Cls, env) => {
    const layout = new Cls(240, 320, 56, 'vertical');
    const items: Array<{ v: number }> = [];
    for (let i = 0; i < 20; i++) {
      const it = { v: randVal() };
      items.push(it);
      layout.addItem(it);
    }
    let dragging = false;
    let lastY = 0;
    const down = (e: Event) => { dragging = true; lastY = (e as PointerEvent).clientY; };
    const move = (e: Event) => {
      if (!dragging) return;
      const y = (e as PointerEvent).clientY;
      layout.scrollBy(-(y - lastY));
      lastY = y;
    };
    const up = () => { dragging = false; };
    env.canvas.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return {
      hint: '按住拖动列表上下滚动',
      render: (ctx) => {
        ctx.save();
        ctx.translate(50, 60);
        layout.render(ctx, (c: any, x: number, y: number, w: number, h: number, data: any, i: number) => {
          fillRound(c, x, y + 3, w, h - 6, 8, valColor(data.v));
          label(c, `item ${i}`, x + 12, y + h / 2, '#0f172a', 13);
        });
        ctx.restore();
      },
      getState: () => ({ 条目数: items.length, 滚动偏移: Math.round(layout.getScrollOffset?.() ?? layout._scrollOffset ?? 0) }),
      destroy: () => {
        env.canvas.removeEventListener('pointerdown', down);
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      },
    };
  },

  'stack-layout': (Cls, env) => {
    const layout = new Cls(3, 92, 120, 'vertical');
    let n = 0;
    return {
      hint: '向槽位 push / pop 元素',
      actions: [
        { label: 'push 槽1', run: () => layout.push(0, { v: randVal(), id: ++n }) },
        { label: 'push 槽2', run: () => layout.push(1, { v: randVal(), id: ++n }) },
        { label: 'push 槽3', run: () => layout.push(2, { v: randVal(), id: ++n }) },
        { label: 'pop 槽1', run: () => layout.pop(0) },
        { label: 'pop 槽2', run: () => layout.pop(1) },
        { label: 'pop 槽3', run: () => layout.pop(2) },
      ],
      render: (ctx) => {
        ctx.save();
        ctx.translate(14, 90);
        layout.render(ctx, (c: any, x: number, y: number, w: number, h: number, item: any) => {
          fillRound(c, x + 2, y + 2, w - 4, h - 4, 10, valColor(item?.v || 1));
        });
        ctx.restore();
      },
      getState: () => ({
        槽1: layout.getSlotItems?.(0)?.length ?? '-',
        槽2: layout.getSlotItems?.(1)?.length ?? '-',
        槽3: layout.getSlotItems?.(2)?.length ?? '-',
      }),
    };
  },

  /* ----- layout ----- */
  'container': (Cls) => {
    const root = Cls.create({ name: 'root', x: 0, y: 0, width: LOGICAL_W, height: 360 });
    const panel = Cls.create({ name: 'panel', x: 60, y: 80, width: 220, height: 200 });
    const child = Cls.create({ name: 'child', x: 40, y: 40, width: 90, height: 90 });
    root.addChild(panel);
    panel.addChild(child);
    let angle = 0;
    return {
      hint: '容器树：root > panel > child，panel 持续旋转',
      update: (dt) => {
        angle += dt / 1000;
        panel.rotation = angle;
      },
      render: (ctx) => {
        const drawNode = (node: any, color: string) => {
          const p = root.toGlobal ? nodeToGlobal(node) : { x: node.x, y: node.y };
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(node.rotation || 0);
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.85;
          ctx.fillRect(-(node.width || 0) / 2, -(node.height || 0) / 2, node.width || 0, node.height || 0);
          ctx.restore();
        };
        const nodeToGlobal = (node: any): { x: number; y: number } => {
          let x = node.x || 0;
          let y = node.y || 0;
          let p = node.parent;
          while (p) { x += p.x || 0; y += p.y || 0; p = p.parent; }
          return { x, y };
        };
        drawNode(panel, '#22d3ee');
        drawNode(child, '#f472b6');
        label(ctx, `children(root)=${root.getChildren().length} children(panel)=${panel.getChildren().length}`, 20, 330, '#94a3b8');
      },
      getState: () => ({ root子节点: root.getChildren().length, 旋转: `${Math.round((angle * 180) / Math.PI) % 360}°` }),
    };
  },

  /* ----- input ----- */
  // key 必须与 atoms-registry.json 的 canonical key 保持一致。
  // 旧的 tap-input 名称会让图鉴把 tap 当成“暂无专用演示”。
  'tap': (Cls, env) => {
    const taps: Array<{ x: number; y: number; t: number; multi?: boolean }> = [];
    const inst = new Cls(env.canvas);
    inst.onTap((x: number, y: number) => taps.push({ x, y, t: 300 }));
    inst.onDoubleTap?.((x: number, y: number) => taps.push({ x, y, t: 500, multi: true }));
    inst.onMultiTap?.((_pts: any, x: number, y: number) => taps.push({ x, y, t: 500, multi: true }));
    return {
      hint: '点击 / 双击画布，观察触点涟漪',
      update: (dt) => { taps.forEach((t) => { t.t -= dt; }); while (taps.length && taps[0].t <= 0) taps.shift(); },
      render: (ctx) => {
        taps.forEach((t) => {
          const k = t.t / 500;
          ctx.beginPath();
          ctx.arc(t.x, t.y, 10 + (1 - k) * 26, 0, Math.PI * 2);
          ctx.strokeStyle = t.multi ? '#f472b6' : '#22d3ee';
          ctx.globalAlpha = Math.max(k, 0.1);
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.globalAlpha = 1;
        });
      },
      getState: () => ({ 累计触点: taps.length }),
      destroy: () => inst.destroy?.(),
    };
  },

  'drag-swap': (Cls, env) => {
    const grid = { cols: 5, rows: 5, cellSize: 52, ox: 20, oy: 70 };
    const vals: number[][] = Array.from({ length: 5 }, () => Array.from({ length: 5 }, randVal));
    const inst = new Cls(env.canvas, { threshold: 10, directions: ['up', 'down', 'left', 'right'] });
    inst.setGridResolver((x: number, y: number) => {
      const col = Math.floor((x - grid.ox) / grid.cellSize);
      const row = Math.floor((y - grid.oy) / grid.cellSize);
      return col >= 0 && row >= 0 && col < 5 && row < 5 ? { col, row } : null;
    });
    let swaps = 0;
    inst.onDragEnd((from: any, to: any) => {
      if (!from || !to) return;
      const t = vals[from.row][from.col];
      vals[from.row][from.col] = vals[to.row][to.col];
      vals[to.row][to.col] = t;
      swaps++;
    });
    return {
      hint: '按住一个格子向相邻方向拖动，松手交换',
      render: (ctx) => {
        for (let r = 0; r < 5; r++) {
          for (let c = 0; c < 5; c++) {
            fillRound(ctx, grid.ox + c * grid.cellSize + 2, grid.oy + r * grid.cellSize + 2, grid.cellSize - 4, grid.cellSize - 4, 8, valColor(vals[r][c]));
          }
        }
      },
      getState: () => ({ 已交换: swaps }),
      destroy: () => inst.destroy?.(),
    };
  },

  'shoot-aim': (Cls, env) => {
    const inst = new Cls(env.canvas, {});
    inst.setOrigin(LOGICAL_W / 2, 380);
    let aiming: { angle: number; power: number } | null = null;
    const balls: Array<{ x: number; y: number; vx: number; vy: number }> = [];
    inst.onAimMove((angle: number, power: number) => { aiming = { angle, power }; });
    inst.onShoot((angle: number, power: number, origin: any) => {
      aiming = null;
      const sp = 0.3 + power * 0.35;
      balls.push({ x: origin?.x ?? LOGICAL_W / 2, y: origin?.y ?? 380, vx: Math.cos(angle) * sp, vy: Math.sin(angle) * sp });
    });
    return {
      hint: '按住拖瞄准，松手发射小球（重力抛物线）',
      update: (dt) => {
        balls.forEach((b) => { b.x += b.vx * dt; b.y += b.vy * dt; b.vy += 0.0009 * dt; });
        for (let i = balls.length - 1; i >= 0; i--) if (balls[i].y > 460) balls.splice(i, 1);
      },
      render: (ctx) => {
        ctx.fillStyle = '#fbbf24';
        ctx.beginPath(); ctx.arc(LOGICAL_W / 2, 380, 12, 0, Math.PI * 2); ctx.fill();
        if (aiming) {
          const pts = inst.getAimLine ? inst.getAimLine(120, 8) : [];
          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = '#22d3ee';
          ctx.beginPath();
          pts.forEach((p: any, i: number) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); });
          ctx.stroke();
          ctx.setLineDash([]);
        }
        balls.forEach((b) => {
          ctx.fillStyle = '#f472b6';
          ctx.beginPath(); ctx.arc(b.x, b.y, 7, 0, Math.PI * 2); ctx.fill();
        });
      },
      getState: () => ({ 场上球: balls.length, 瞄准中: aiming ? '是' : '否' }),
      destroy: () => inst.destroy?.(),
    };
  },

  'swipe-direction': (Cls, env) => {
    const inst = new Cls(env.canvas, { threshold: 24 });
    const box = { x: 145, y: 180, tx: 145, ty: 180 };
    let count = 0;
    let last = '-';
    inst.onSwipe((dir: string) => {
      count++;
      last = dir;
      const d = 60;
      if (dir === 'left') box.tx = Math.max(20, box.tx - d);
      if (dir === 'right') box.tx = Math.min(270, box.tx + d);
      if (dir === 'up') box.ty = Math.max(60, box.ty - d);
      if (dir === 'down') box.ty = Math.min(360, box.ty + d);
    });
    return {
      hint: '在画布上快速滑动，方块朝滑动方向移动',
      update: (dt) => {
        const k = Math.min(1, dt / 120);
        box.x += (box.tx - box.x) * k;
        box.y += (box.ty - box.y) * k;
      },
      render: (ctx) => { fillRound(ctx, box.x, box.y, 50, 50, 10, '#a3e635'); },
      getState: () => ({ 滑动次数: count, 最近方向: last }),
      destroy: () => inst.destroy?.(),
    };
  },

  'slide-trace': (Cls, env) => {
    const inst = new Cls(env.canvas, {});
    let points: Array<{ x: number; y: number }> = [];
    let strokes = 0;
    inst.onTraceMove((_x: number, _y: number, pts: any) => { points = Array.isArray(pts) ? pts.slice() : points; });
    inst.onTraceEnd(() => { strokes++; });
    return {
      hint: '按住拖动绘制轨迹（slide-trace 采样点列）',
      render: (ctx) => {
        if (points.length > 1) {
          ctx.strokeStyle = '#c084fc';
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';
          ctx.beginPath();
          points.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); });
          ctx.stroke();
        }
      },
      getState: () => ({ 笔画数: strokes, 当前点数: points.length, 轨迹长: Math.round(inst.getTraceLength?.() ?? 0) }),
      destroy: () => inst.destroy?.(),
    };
  },

  'hold-release': (Cls, env) => {
    const inst = new Cls(env.canvas, {});
    let progress = 0;
    let power = 0;
    let releases = 0;
    inst.onHoldProgress((p: number) => { progress = typeof p === 'number' ? p : 0; });
    inst.onRelease((pw: number) => { power = typeof pw === 'number' ? pw : progress; progress = 0; releases++; });
    return {
      hint: '按住充能，松手释放（圆环=当前充能，竖条=上次力度）',
      render: (ctx) => {
        ctx.beginPath();
        ctx.arc(170, 190, 60, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(progress, 1));
        ctx.strokeStyle = '#22d3ee';
        ctx.lineWidth = 10;
        ctx.stroke();
        fillRound(ctx, 290, 320 - Math.min(power, 1) * 240, 20, Math.min(power, 1) * 240, 4, '#f472b6');
      },
      getState: () => ({ 充能: `${Math.round(Math.min(progress, 1) * 100)}%`, 上次力度: power.toFixed(2), 释放次数: releases }),
      destroy: () => inst.destroy?.(),
    };
  },

  /* ----- match ----- */
  'line-match': (Cls, env) => {
    const grid = makeStubGrid(6, 6, 4);
    const inst = new Cls({ minLength: 3, directions: ['horizontal', 'vertical'] });
    let hl = new Set<string>();
    let removed = 0;
    const detect = () => {
      const matches = inst.findMatches(grid) || [];
      hl = new Set<string>();
      matches.forEach((m: any) => (m.cells || m).forEach((cell: any) => hl.add(`${cell.col},${cell.row}`)));
      return matches;
    };
    const off = onTap(env, (x, y) => {
      const c = Math.floor((x - grid.offsetX) / grid.cellSize);
      const r = Math.floor((y - grid.offsetY) / grid.cellSize);
      if (grid.isValid(c, r)) grid.setCell(c, r, randVal(4));
    });
    return {
      hint: '按钮检测并高亮三连；再次点击消除并补新值',
      actions: [
        { label: '检测三连', run: () => { detect(); } },
        {
          label: '消除+补充', run: () => {
            const matches = detect();
            removed += hl.size;
            inst.removeMatches(grid, matches);
            grid.forEachCell((v, c, r) => { if (v === null || v === undefined) grid.setCell(c, r, randVal(4)); });
            hl = new Set();
          },
        },
      ],
      render: (ctx) => drawStubGrid(ctx, grid, hl),
      getState: () => ({ 高亮格: hl.size, 累计消除: removed }),
      destroy: off,
    };
  },

  'bfs-flood': (Cls, env) => {
    const grid = makeStubGrid(6, 6, 3);
    const inst = new Cls({});
    let hl = new Set<string>();
    let groupSize = 0;
    const off = onTap(env, (x, y) => {
      const c = Math.floor((x - grid.offsetX) / grid.cellSize);
      const r = Math.floor((y - grid.offsetY) / grid.cellSize);
      if (!grid.isValid(c, r)) return;
      const cells = inst.findGroup(grid, c, r) || [];
      hl = new Set(cells.map((cell: any) => `${cell.col},${cell.row}`));
      groupSize = cells.length;
    });
    return {
      hint: '点击任意格，高亮同色连通块（BFS 洪水填充）',
      render: (ctx) => drawStubGrid(ctx, grid, hl),
      getState: () => ({ 连通块大小: groupSize }),
      destroy: off,
    };
  },

  'pair-match': (Cls, env) => {
    const grid = makeStubGrid(6, 6, 4);
    const inst = new Cls({});
    let sel: any = null;
    let path: any[] = [];
    let cleared = 0;
    const off = onTap(env, (x, y) => {
      const c = Math.floor((x - grid.offsetX) / grid.cellSize);
      const r = Math.floor((y - grid.offsetY) / grid.cellSize);
      if (!grid.isValid(c, r) || grid.getCell(c, r) === null) return;
      if (!sel) { sel = { col: c, row: r }; return; }
      if (sel.col === c && sel.row === r) { sel = null; return; }
      if (inst.canMatch(grid, sel, { col: c, row: r })) {
        path = inst.findPath(grid, sel, { col: c, row: r }) || [];
        grid.setCell(sel.col, sel.row, null);
        grid.setCell(c, r, null);
        cleared += 2;
        setTimeout(() => { path = []; }, 400);
      }
      sel = null;
    });
    return {
      hint: '连连看：依次点击两个相同值格子，可连通则消除',
      render: (ctx) => {
        const hl = sel ? new Set([`${sel.col},${sel.row}`]) : undefined;
        drawStubGrid(ctx, grid, hl);
        if (path.length > 1) {
          ctx.strokeStyle = '#fbbf24';
          ctx.lineWidth = 4;
          ctx.beginPath();
          path.forEach((p: any, i: number) => {
            const px = grid.offsetX + p.col * grid.cellSize + grid.cellSize / 2;
            const py = grid.offsetY + p.row * grid.cellSize + grid.cellSize / 2;
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          });
          ctx.stroke();
        }
      },
      getState: () => ({ 已消除: cleared, 剩余可配对: inst.hasAnyPair ? String(!!inst.hasAnyPair(grid)) : '-' }),
      destroy: off,
    };
  },

  // CollisionDetect 的注册 key 是 collision，不是 className。
  'collision': (Cls) => {
    const inst = new Cls({});
    const ball = { id: 'ball', x: 60, y: 120, r: 16, vx: 0.12, vy: 0.09 };
    const statics = [
      { id: 's1', x: 220, y: 100, r: 22 },
      { id: 's2', x: 120, y: 280, r: 18 },
      { id: 's3', x: 250, y: 300, r: 26 },
    ];
    let hits: string[] = [];
    return {
      hint: '运动圆与静止圆碰撞时变红高亮',
      update: (dt) => {
        ball.x += ball.vx * dt;
        ball.y += ball.vy * dt;
        if (ball.x < 20 || ball.x > 320) ball.vx *= -1;
        if (ball.y < 40 || ball.y > 400) ball.vy *= -1;
        const found = inst.findCollisions(statics, ball) || [];
        hits = found.map((f: any) => f.id || f);
      },
      render: (ctx) => {
        statics.forEach((s) => {
          ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = hits.includes(s.id) ? '#f87171' : '#334155';
          ctx.fill();
        });
        ctx.beginPath(); ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
        ctx.fillStyle = '#22d3ee';
        ctx.fill();
      },
      getState: () => ({ 碰撞中: hits.length ? hits.join(',') : '无' }),
    };
  },

  'path-link': (Cls, env) => {
    const grid = makeStubGrid(6, 6, 3);
    const inst = new Cls({});
    let drawing = false;
    const off = onTap(env, (x, y) => {
      const c = Math.floor((x - grid.offsetX) / grid.cellSize);
      const r = Math.floor((y - grid.offsetY) / grid.cellSize);
      if (!grid.isValid(c, r)) return;
      const v = grid.getCell(c, r);
      if (!drawing) {
        drawing = true;
        inst.startPath(c, r, v);
      } else {
        const path = inst.getPath() || [];
        const first = path[0];
        if (first && v === first.value) inst.extendPath(c, r);
        else { inst.reset(); drawing = true; inst.startPath(c, r, v); }
      }
    });
    return {
      hint: '依次点击同值相邻格延伸路径；按钮完成/重置',
      actions: [
        { label: '完成路径', run: () => { inst.completePath?.(); drawing = false; } },
        { label: '重置', run: () => { inst.reset(); drawing = false; } },
      ],
      render: (ctx) => {
        drawStubGrid(ctx, grid);
        const path = inst.getPath() || [];
        if (path.length > 0) {
          ctx.strokeStyle = '#f472b6';
          ctx.lineWidth = 5;
          ctx.beginPath();
          path.forEach((p: any, i: number) => {
            const px = grid.offsetX + p.col * grid.cellSize + grid.cellSize / 2;
            const py = grid.offsetY + p.row * grid.cellSize + grid.cellSize / 2;
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          });
          ctx.stroke();
        }
      },
      getState: () => ({ 路径长: (inst.getPath() || []).length }),
      destroy: off,
    };
  },

  'physics-drop': (Cls) => {
    const inst = new Cls({ gravity: 0.0015 });
    inst.setFloor?.(400);
    inst.setWalls?.(20, 320);
    let n = 0;
    const add = () => {
      n++;
      inst.addBody(`b${n}`, 60 + Math.random() * 220, 30, 10 + Math.random() * 10, {});
    };
    for (let i = 0; i < 5; i++) add();
    return {
      hint: '刚体受重力下落，在地板/墙壁内弹跳堆叠',
      actions: [{ label: '加球', run: add }],
      update: (dt) => inst.update(Math.min(dt, 50)),
      render: (ctx) => {
        ctx.strokeStyle = '#475569';
        ctx.strokeRect(20, 20, 300, 380);
        (inst.getBodies() || []).forEach((b: any) => {
          ctx.beginPath(); ctx.arc(b.x, b.y, b.radius ?? b.r ?? 10, 0, Math.PI * 2);
          ctx.fillStyle = PALETTE[Math.abs(hash(b.id)) % PALETTE.length];
          ctx.fill();
        });
      },
      getState: () => ({ 刚体数: (inst.getBodies() || []).length, 已静止: String(!!inst.isSettled?.()) }),
    };
    function hash(s: string): number {
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
      return h;
    }
  },

  /* ----- spawn ----- */
  'top-drop': (Cls) => {
    const grid = makeStubGrid(6, 6, 5, 44, 20, 70);
    const inst = new Cls(grid, { speed: 1, stagger: 30 });
    let animating = false;
    let progress = 0;
    const dig = () => {
      let holes = 0;
      for (let i = 0; i < 8; i++) {
        const c = Math.floor(Math.random() * 6);
        const r = Math.floor(Math.random() * 6);
        if (grid.getCell(c, r) !== null) { grid.setCell(c, r, null); holes++; }
      }
      return holes;
    };
    const drop = () => {
      const drops = inst.calculateDrops ? inst.calculateDrops() : [];
      const news = inst.generateNew ? inst.generateNew(5) : [];
      if (inst.applyDrops) inst.applyDrops(drops, news);
      progress = 0;
      animating = true;
    };
    return {
      hint: '挖洞 → 触发下落：上方方块下落补位，顶部生成新块',
      actions: [{ label: '随机挖洞', run: dig }, { label: '触发下落', run: drop }],
      update: (dt) => {
        if (animating) {
          progress += dt / 500;
          if (progress >= 1) { progress = 1; animating = false; }
        }
      },
      render: (ctx) => {
        if (animating && inst.animate) {
          inst.animate(ctx, progress, (c: any, x: number, y: number, v: number) => {
            fillRound(c, x, y, 40, 40, 8, valColor(v));
          });
        } else {
          drawStubGrid(ctx, grid);
        }
      },
      getState: () => ({ 动画中: String(animating) }),
    };
  },

  'random-spawn': (Cls) => {
    const inst = new Cls({ x: 20, y: 60, w: 300, h: 320 }, { maxCount: 12, interval: 700 });
    let spawned = 0;
    inst.onSpawn?.(() => { spawned++; });
    return {
      hint: '按间隔在区域内随机生成方块，达到上限自动停',
      actions: [
        { label: '加速', run: () => inst.setRate?.(250) },
        { label: '清空', run: () => { (inst.getActive() || []).slice().forEach((e: any) => inst.remove(e.id)); spawned = 0; } },
      ],
      update: (dt) => inst.update(dt),
      render: (ctx) => {
        ctx.strokeStyle = '#334155';
        ctx.strokeRect(20, 60, 300, 320);
        (inst.getActive() || []).forEach((e: any) => fillRound(ctx, e.x - 10, e.y - 10, 20, 20, 5, '#a3e635'));
      },
      getState: () => ({ 场上: (inst.getActive() || []).length, 累计生成: spawned }),
    };
  },

  'wave-spawn': (Cls) => {
    const inst = new Cls({
      waves: [
        { count: 4, interval: 500, types: [1], speed: 0.05 },
        { count: 6, interval: 350, types: [2], speed: 0.07 },
        { count: 8, interval: 250, types: [3], speed: 0.09 },
      ],
    });
    const alive: Array<{ x: number; y: number; v: number }> = [];
    let waveIdx = 0;
    let done = false;
    inst.onWaveComplete?.(() => { waveIdx++; });
    inst.onAllComplete?.(() => { done = true; });
    return {
      hint: '三波敌人按节奏从顶部入场并下移',
      actions: [{ label: '开始/下一波', run: () => { if (inst.startWave) inst.startWave(waveIdx); else inst.nextWave?.(); } }],
      update: (dt) => {
        const news = inst.update(dt);
        if (Array.isArray(news)) {
          news.forEach((e: any) => alive.push({ x: 40 + Math.random() * 260, y: 40, v: e.type ?? e.v ?? (waveIdx + 1) }));
        }
        const sp = 0.04 + waveIdx * 0.02;
        alive.forEach((e) => { e.y += sp * dt; });
        for (let i = alive.length - 1; i >= 0; i--) if (alive[i].y > 420) alive.splice(i, 1);
      },
      render: (ctx) => {
        alive.forEach((e) => fillRound(ctx, e.x - 12, e.y - 12, 24, 24, 6, valColor(e.v)));
      },
      getState: () => ({ 当前波: Math.min(waveIdx + 1, 3), 场上: alive.length, 全部完成: String(done) }),
    };
  },

  'merge-upgrade': (Cls) => {
    const inst = new Cls({});
    let item = { level: 1, type: 'gem', value: 10 };
    let merges = 0;
    const cfg = inst.getLevelConfig?.(item.level) || {};
    return {
      hint: '两个同级元素合成升级，等级配色来自 getLevelConfig',
      actions: [
        {
          label: '合成升级', run: () => {
            if (inst.isMaxLevel?.(item.level)) return;
            if (inst.canMerge ? inst.canMerge(item, item) : true) {
              const r = inst.merge([item, item]);
              if (r) { item = { level: r.level ?? item.level + 1, type: r.type ?? 'gem', value: r.value ?? item.value * 2 }; merges++; }
            }
          },
        },
        { label: '重置', run: () => { item = { level: 1, type: 'gem', value: 10 }; merges = 0; } },
      ],
      render: (ctx) => {
        const c = inst.getLevelConfig?.(item.level) || {};
        const size = c.size || 40 + item.level * 10;
        ctx.beginPath();
        ctx.arc(170, 200, size / 2, 0, Math.PI * 2);
        ctx.fillStyle = c.color || PALETTE[(item.level - 1) % PALETTE.length];
        ctx.fill();
        label(ctx, `Lv.${item.level} ${c.name || ''}`, 170, 290, '#e2e8f0', 16, 'center');
        label(ctx, `value=${item.value}`, 170, 315, '#94a3b8', 12, 'center');
      },
      getState: () => ({ 等级: item.level, 合成次数: merges, 已满级: String(!!inst.isMaxLevel?.(item.level)), 配置: cfg.name || '-' }),
    };
  },

  /* ----- flow ----- */
  'score-system': (Cls) => {
    const inst = new Cls({ milestones: [500, 1000, 2000] });
    let milestoneMsg = '';
    inst.onMilestone?.((m: number) => { milestoneMsg = `达成里程碑 ${m}!`; });
    return {
      hint: '加分与连击；到达里程碑触发回调',
      actions: [
        { label: '+100', run: () => inst.addScore(100) },
        { label: '+连击', run: () => inst.addScore(50, true) },
        { label: '重置', run: () => { inst.reset(); milestoneMsg = ''; } },
      ],
      update: (dt) => inst.update?.(dt),
      render: (ctx) => {
        label(ctx, String(inst.getScore()), 170, 180, '#fbbf24', 44, 'center');
        label(ctx, `COMBO ×${inst.getCombo?.() ?? 0}`, 170, 225, '#f472b6', 18, 'center');
        if (milestoneMsg) label(ctx, milestoneMsg, 170, 265, '#a3e635', 14, 'center');
      },
      getState: () => ({ 分数: inst.getScore(), 连击: inst.getCombo?.() ?? 0 }),
    };
  },

  'timer-countdown': (Cls) => {
    const inst = new Cls(30, { warningAt: 10 });
    let expired = false;
    inst.onExpire?.(() => { expired = true; });
    inst.start();
    return {
      hint: '30 秒倒计时（atom 自带 render）',
      actions: [
        { label: '暂停', run: () => inst.pause() },
        { label: '继续', run: () => inst.resume() },
        { label: '重置', run: () => { inst.reset(); expired = false; inst.start(); } },
      ],
      update: (dt) => inst.update(dt),
      render: (ctx) => {
        inst.render(ctx, 170, 180, { size: 48 });
        label(ctx, expired ? '已到期' : `剩余 ${Math.ceil(inst.getRemaining())}s`, 170, 250, expired ? '#f87171' : '#94a3b8', 14, 'center');
      },
      getState: () => ({ 剩余: `${inst.getRemaining().toFixed(1)}s`, 进度: `${Math.round((inst.getProgress?.() ?? 0) * 100)}%`, 已到期: String(expired) }),
    };
  },

  'moves-limit': (Cls) => {
    const inst = new Cls(8, {});
    return {
      hint: '步数限制：每步消耗一点，耗尽即止',
      actions: [
        { label: '走一步', run: () => inst.useMove() },
        { label: '重置', run: () => inst.reset?.() },
      ],
      render: (ctx) => {
        const remain = inst.getRemaining();
        for (let i = 0; i < 8; i++) {
          ctx.beginPath();
          ctx.arc(50 + (i % 4) * 80, 160 + Math.floor(i / 4) * 80, 22, 0, Math.PI * 2);
          ctx.fillStyle = i < remain ? '#22d3ee' : '#1e293b';
          ctx.fill();
        }
        label(ctx, remain <= 0 ? '步数耗尽' : `剩余 ${remain} 步`, 170, 320, remain <= 0 ? '#f87171' : '#94a3b8', 16, 'center');
      },
      getState: () => ({ 剩余步数: inst.getRemaining() }),
    };
  },

  'level-progress': (Cls) => {
    const inst = new Cls({ target: 100 });
    let completed = false;
    inst.onComplete?.(() => { completed = true; });
    return {
      hint: '关卡进度条（atom 自带 render，支持里程碑）',
      actions: [
        { label: '+10 进度', run: () => inst.addProgress(10) },
        { label: '重置', run: () => { inst.reset(); completed = false; } },
      ],
      render: (ctx) => {
        inst.render(ctx, 40, 180, 260, 24, {});
        if (completed) label(ctx, '关卡完成!', 170, 240, '#a3e635', 18, 'center');
      },
      getState: () => ({ 进度: `${Math.round((inst.getProgress?.() ?? 0) * 100)}%`, 完成: String(completed) }),
    };
  },

  'difficulty-ramp': (Cls) => {
    const inst = new Cls({ interval: 4000, maxLevel: 10 });
    return {
      hint: '随时间自动提升难度等级，adjustParam 按比例缩放基础值',
      actions: [
        { label: '直接到5级', run: () => inst.setLevel?.(5) },
        { label: '重置', run: () => inst.reset?.() },
      ],
      update: (dt) => inst.update(dt),
      render: (ctx) => {
        const lv = inst.getLevel?.() ?? 1;
        for (let i = 0; i < 10; i++) {
          fillRound(ctx, 30 + i * 29, 200 - i * 8, 22, 20 + i * 8, 4, i < lv ? '#f472b6' : '#1e293b');
        }
        label(ctx, `spawn 间隔示例: ${Math.round(inst.adjustParam ? inst.adjustParam(1000) : 1000)}ms`, 170, 300, '#94a3b8', 13, 'center');
      },
      getState: () => ({ 等级: inst.getLevel?.() ?? 1, 倍率: (inst.getMultiplier?.() ?? 1).toFixed(2) }),
    };
  },

  /* ----- fx ----- */
  'particle-burst': (Cls, env) => {
    const inst = new Cls({ count: 30 });
    const off = onTap(env, (x, y) => inst.emit(x, y, {}));
    return {
      hint: '点击画布触发粒子爆发',
      actions: [{ label: '中心爆发', run: () => inst.emit(170, 200, { count: 50 }) }],
      update: (dt) => inst.update(dt),
      render: (ctx) => inst.render(ctx),
      destroy: off,
    };
  },

  // TweenAnim 的注册 key 是 tween，不是 className。
  'tween': (Cls) => {
    const inst = new Cls();
    const box = { x: 60, y: 100, s: 44 };
    const move = () => {
      inst.add(box, { x: 30 + Math.random() * 240, y: 60 + Math.random() * 280 }, 700, 'easeOutBounce');
    };
    return {
      hint: '按钮触发缓动位移（easeOutBounce）',
      actions: [{ label: '随机移动', run: move }, { label: '清除动画', run: () => inst.clear?.() }],
      update: (dt) => inst.update(dt),
      render: (ctx) => fillRound(ctx, box.x, box.y, box.s, box.s, 8, '#c084fc'),
      getState: () => ({ 动画中: String(!!inst.isActive?.()) }),
    };
  },

  'score-float': (Cls, env) => {
    const inst = new Cls({});
    let n = 0;
    const off = onTap(env, (x, y) => { n++; inst.show(x, y, `+${n * 100}`, '#fbbf24'); });
    return {
      hint: '点击画布飘出加分文字',
      actions: [{ label: '连击飘分 ×5', run: () => { for (let i = 0; i < 5; i++) { n++; inst.show(80 + i * 45, 200, `+${n * 100}`, '#fbbf24'); } } }],
      update: (dt) => inst.update(dt),
      render: (ctx) => inst.render(ctx),
      getState: () => ({ 累计飘分: n }),
      destroy: off,
    };
  },

  'combo-text': (Cls) => {
    const inst = new Cls({});
    inst.setPosition?.(170, 200);
    let combo = 0;
    return {
      hint: '连击数字越大，文字越醒目',
      actions: [
        { label: '连击+1', run: () => { combo++; inst.show(combo, `${combo} COMBO!`); } },
        { label: '重置', run: () => { combo = 0; inst.reset?.(); } },
      ],
      update: (dt) => inst.update(dt),
      render: (ctx) => inst.render(ctx),
      getState: () => ({ 当前连击: combo }),
    };
  },

  'screen-shake': (Cls) => {
    const inst = new Cls({});
    return {
      hint: '震屏时参照网格随之抖动',
      actions: [
        { label: '轻震', run: () => inst.shake(4, 300) },
        { label: '强震', run: () => inst.shake(12, 600) },
      ],
      update: (dt) => inst.update(dt),
      render: (ctx) => {
        inst.applyToContext(ctx);
        ctx.strokeStyle = '#334155';
        for (let x = 20; x <= 320; x += 30) { ctx.beginPath(); ctx.moveTo(x, 40); ctx.lineTo(x, 400); ctx.stroke(); }
        for (let y = 40; y <= 400; y += 30) { ctx.beginPath(); ctx.moveTo(20, y); ctx.lineTo(320, y); ctx.stroke(); }
      },
      getState: () => ({ 震动中: String(!!inst.isShaking?.()) }),
    };
  },

  'progress-bar': (Cls) => {
    const inst = new Cls(40, 200, 260, 22, { color: '#22d3ee' });
    inst.setMilestones?.([{ at: 0.33, label: '33%' }, { at: 0.66, label: '66%' }, { at: 1, label: '满' }]);
    let p = 0;
    return {
      hint: '目标进度平滑追赶（atom 自带 render + 里程碑刻度）',
      actions: [
        { label: '+20%', run: () => { p = Math.min(1, p + 0.2); inst.setTarget(p); } },
        { label: '清零', run: () => { p = 0; inst.setProgress ? inst.setProgress(0) : inst.setTarget(0); } },
      ],
      update: (dt) => inst.update?.(dt),
      render: (ctx) => inst.render(ctx),
      getState: () => ({ 目标: `${Math.round(p * 100)}%` }),
    };
  },

  'sprite-animation': (Cls) => {
    const inst = new Cls({
      x: 170, y: 200, width: 96, height: 96,
      anchor: { x: 0.5, y: 0.5 }, frameInterval: 90, infiniteLoop: true,
    });
    inst.loadFramesFromData(makeSpinFrames());
    inst.play();
    return {
      hint: '程序化生成 8 帧 dataURL，循环播放帧动画',
      actions: [
        { label: '播放', run: () => inst.play() },
        { label: '暂停', run: () => inst.pause() },
        { label: '停止', run: () => inst.stop() },
      ],
      update: (dt) => inst.update(dt),
      render: (ctx) => inst.render(ctx),
      getState: () => ({ 帧: `${inst.currentFrame}/${inst.totalFrames}`, 播放中: String(inst.isPlaying) }),
    };
  },

  /* ----- capability ----- */
  'image-loader': (Cls) => {
    const inst = new Cls();
    let ready = false;
    inst.load({ gem: makeGem('#22d3ee'), star: makeGem('#fbbf24', true), heart: makeGem('#f472b6') }, () => { ready = true; });
    return {
      hint: 'load(slots) 加载 dataURL 图片，draw(ctx,key) 绘制',
      render: (ctx) => {
        if (!ready) { label(ctx, '加载中…', 170, 200, '#94a3b8', 14, 'center'); return; }
        const keys = ['gem', 'star', 'heart'];
        keys.forEach((k, i) => {
          const ok = inst.draw(ctx, k, 50 + i * 90, 160, 64, 64);
          if (!ok) label(ctx, `${k} 未就绪`, 82 + i * 90, 192, '#f87171', 11, 'center');
        });
        label(ctx, '全部就绪', 170, 260, '#a3e635', 13, 'center');
      },
      getState: () => ({ 就绪: String(ready), 槽位: Object.keys(inst.getSlots?.() || {}).length }),
    };
  },

  'audio-engine': (Cls) => {
    const inst = new Cls();
    inst.init?.();
    let last = '-';
    const play = (name: string) => () => { inst.unlock?.(); inst[name]?.(); last = name; };
    return {
      hint: '按钮触发各类音效（WebAudio 合成，需用户手势解锁）',
      actions: ['playClick', 'playSwap', 'playMatch', 'playCombo', 'playFail', 'playScore', 'playWin', 'playCrash']
        .map((n) => ({ label: n.replace('play', ''), run: play(n) })),
      render: (ctx) => {
        label(ctx, 'AudioEngine', 170, 170, '#e2e8f0', 20, 'center');
        label(ctx, `最近播放: ${last}`, 170, 210, '#22d3ee', 14, 'center');
      },
      getState: () => ({ 最近: last }),
      destroy: () => inst.destroy?.(),
    };
  },

  'ui-overlay': (Cls) => {
    const inst = new Cls();
    inst.setThemeColors?.({ primary: '#22d3ee', accent: '#f472b6' });
    let mode: 'hud' | 'cta' | 'guide' | 'end' = 'hud';
    let t = 0;
    return {
      hint: 'HUD / CTA / 引导手 / 结算屏 四种 UI 覆盖层',
      actions: (['hud', 'cta', 'guide', 'end'] as const).map((m) => ({ label: m.toUpperCase(), run: () => { mode = m; } })),
      update: (dt) => { t += dt; },
      render: (ctx) => {
        if (mode === 'hud') inst.renderHUD?.(ctx, 12340, 3, {});
        else if (mode === 'cta') inst.renderCTA?.(ctx, 70, 180, 200, 60, '立即安装');
        else if (mode === 'guide') inst.renderGuide?.(ctx, 170 + Math.sin(t / 300) * 40, 220);
        else inst.renderEndScreen?.(ctx, true, '恭喜通关!');
      },
      getState: () => ({ 模式: mode }),
    };
  },

  'audio-sfx': (Cls) => {
    const inst = new Cls();
    let last = '-';
    let volume = 1;
    return {
      hint: '音效原子：点击/得分/胜利/碰撞 + 音量控制',
      actions: [
        ...(['playClick', 'playScore', 'playWin', 'playCrash'] as const).map((n) => ({
          label: n.replace('play', ''), run: () => { inst[n]?.(); last = n; },
        })),
        { label: '音量50%', run: () => { volume = 0.5; inst.setVolume?.(0.5); } },
        { label: '音量100%', run: () => { volume = 1; inst.setVolume?.(1); } },
      ],
      render: (ctx) => {
        label(ctx, 'AudioSFX', 170, 170, '#e2e8f0', 20, 'center');
        label(ctx, `最近: ${last}  音量: ${Math.round(volume * 100)}%`, 170, 210, '#22d3ee', 14, 'center');
      },
      getState: () => ({ 最近: last, 音量: `${Math.round(volume * 100)}%` }),
    };
  },

  'audio-bgm': (Cls) => {
    const inst = new Cls();
    const url = silentWavUrl();
    let playing = false;
    return {
      hint: 'BGM 循环播放（演示使用内置静音 wav，验证加载链路）',
      actions: [
        { label: '播放', run: () => { inst.play?.(url); playing = true; } },
        { label: '停止', run: () => { inst.stop?.(); inst.pause?.(); playing = false; } },
      ],
      render: (ctx) => {
        ctx.beginPath(); ctx.arc(170, 190, 50, 0, Math.PI * 2);
        ctx.strokeStyle = playing ? '#a3e635' : '#334155';
        ctx.lineWidth = 6;
        ctx.stroke();
        label(ctx, playing ? '♪ 循环中' : '已停止', 170, 190, playing ? '#a3e635' : '#64748b', 15, 'center');
      },
      getState: () => ({ 状态: playing ? '循环播放' : '停止' }),
      destroy: () => { inst.stop?.(); inst.pause?.(); },
    };
  },

  'haptic-vibration': (Cls) => {
    const inst = new Cls();
    let fired = '-';
    const fire = (pattern: number | number[], name: string) => () => {
      const fn = ['vibrate', 'pulse', 'impact', 'trigger'].map((m) => inst[m]).find((f) => typeof f === 'function');
      if (fn) fn.call(inst, pattern);
      else if (navigator.vibrate) navigator.vibrate(pattern as number);
      fired = name;
    };
    return {
      hint: '振动反馈（仅支持的移动设备生效，桌面端静默）',
      actions: [
        { label: '短振', run: fire(30, '短振30ms') },
        { label: '长振', run: fire(200, '长振200ms') },
        { label: '双振', run: fire([50, 60, 50], '双振') },
      ],
      render: (ctx) => {
        label(ctx, 'HapticVibration', 170, 160, '#e2e8f0', 20, 'center');
        label(ctx, `支持: ${String(!!inst.isSupported?.())}`, 170, 200, '#22d3ee', 14, 'center');
        label(ctx, `最近触发: ${fired}`, 170, 230, '#94a3b8', 13, 'center');
      },
      getState: () => ({ 支持: String(!!inst.isSupported?.()), 最近: fired }),
    };
  },

  'motion-gyroscope': (Cls) => {
    const inst = new Cls();
    return {
      hint: '陀螺仪姿态（桌面端无传感器，移动端可见实时角度）',
      render: (ctx) => {
        const a = inst.getAlpha?.() ?? 0;
        const b = inst.getBeta?.() ?? 0;
        const g = inst.getGamma?.() ?? 0;
        label(ctx, `α(偏航) ${a.toFixed(1)}°`, 170, 140, '#22d3ee', 16, 'center');
        label(ctx, `β(俯仰) ${b.toFixed(1)}°`, 170, 175, '#f472b6', 16, 'center');
        label(ctx, `γ(横滚) ${g.toFixed(1)}°`, 170, 210, '#a3e635', 16, 'center');
        ctx.save();
        ctx.translate(170, 300);
        ctx.rotate((g * Math.PI) / 180);
        fillRound(ctx, -60, -10, 120, 20, 6, '#c084fc');
        ctx.restore();
      },
      getState: () => ({ 支持: String(!!inst.isSupported?.()) }),
      destroy: () => inst.destroy?.(),
    };
  },

  /* ----- system ----- */
  'event-bus': (Cls) => {
    const inst = new Cls();
    const logs: string[] = [];
    const push = (s: string) => { logs.unshift(s); if (logs.length > 9) logs.pop(); };
    let n = 0;
    inst.on('demo.hit', (p: any) => push(`demo.hit → ${JSON.stringify(p)}`));
    inst.on('demo.score', (p: any) => push(`demo.score → ${JSON.stringify(p)}`));
    return {
      hint: '事件总线：emit 触发已订阅的监听器',
      actions: [
        { label: 'emit hit', run: () => inst.emit('demo.hit', { n: ++n }) },
        { label: 'emit score', run: () => inst.emit('demo.score', { value: (n + 1) * 100 }) },
        { label: '清空日志', run: () => { logs.length = 0; } },
      ],
      render: (ctx) => {
        logs.forEach((l, i) => label(ctx, l, 20, 100 + i * 26, i === 0 ? '#22d3ee' : '#64748b', 12));
        if (!logs.length) label(ctx, '尚无事件，点击按钮 emit', 170, 200, '#475569', 13, 'center');
      },
      getState: () => ({ 已触发: n }),
      destroy: () => inst.clear?.(),
    };
  },
};

function genericDemo(key: string): DemoHandle {
  return {
    hint: `atom "${key}" 暂无专用演示，仅展示源码`,
    render: (ctx) => label(ctx, `${key}（无交互演示）`, LOGICAL_W / 2, 200, '#64748b', 14, 'center'),
  };
}

export function createAtomDemo(key: string, Cls: any, env: DemoEnv): DemoHandle {
  const f = demos[key];
  if (!f) return genericDemo(key);
  try {
    return f(Cls, env);
  } catch (e) {
    return {
      hint: `演示初始化失败：${e instanceof Error ? e.message : String(e)}`,
      render: (ctx) => label(ctx, '演示初始化失败（见源码页签）', LOGICAL_W / 2, 200, '#f87171', 13, 'center'),
    };
  }
}
